import { useSitecore } from '@sitecore-content-sdk/nextjs';
import { Other } from '.generated/Feature.CW.model';
import { JSX, useEffect, useRef } from 'react';

export type ScriptsProps = Other.UserScriptItem & {
  params: {
    userScriptType: string;
  };
};

type PhaserSoundLike = {
  stopAll?: () => void;
  removeAll?: () => void;
  context?: {
    close?: () => Promise<void> | void;
    suspend?: () => Promise<void> | void;
  };
};

type PhaserGameLike = {
  sound?: PhaserSoundLike;
  destroy?: (removeCanvas?: boolean) => void;
  canvas?: HTMLCanvasElement;
};

type PhaserNamespaceLike = {
  GAMES?: PhaserGameLike[];
};

const loadScript = (
  script: HTMLScriptElement,
  targetElement: HTMLElement,
  instanceId: string,
  signal?: AbortSignal
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const newScript = document.createElement('script');

    newScript.id = script.id;
    newScript.setAttribute('data-userscripts-id', instanceId);

    // Copy all attributes except async/defer to maintain order
    Array.from(script.attributes).forEach((attr) => {
      if (attr.name === 'src') {
        newScript.src = attr.value;
      } else if (attr.name !== 'async' && attr.name !== 'defer') {
        // Skip async/defer to ensure sequential loading
        newScript.setAttribute(attr.name, attr.value);
      }
    });

    // Set up load and error handlers
    newScript.onload = () => {
      // Execute inline scripts after external scripts load
      if (script.innerHTML) {
        const inlineScript = document.createElement('script');
        inlineScript.setAttribute('data-userscripts-id', instanceId);
        inlineScript.textContent = script.innerHTML;
        if (signal?.aborted) {
          resolve();
          return;
        }
        targetElement.appendChild(inlineScript);
      }
      resolve();
    };
    newScript.onerror = () =>
      reject(new Error(`Failed to load script: ${newScript.src || 'inline script'}`));

    if (signal?.aborted) {
      resolve();
      return;
    }
    targetElement.appendChild(newScript);

    // If it's only an inline script with no src, execute it and resolve
    if (!newScript.src) {
      if (script.innerHTML) {
        const inlineScript = document.createElement('script');
        inlineScript.setAttribute('data-userscripts-id', instanceId);
        inlineScript.textContent = script.innerHTML;
        if (signal?.aborted) {
          resolve();
          return;
        }
        targetElement.appendChild(inlineScript);
      }
      resolve();
    }
  });
};

const injectResources = async (
  javaScript: string,
  targetElement: HTMLElement,
  instanceId: string,
  signal?: AbortSignal
): Promise<void> => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = javaScript;

  // Handle link tags first (CSS)
  const links = tempDiv.querySelectorAll('link');
  links.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && !document.querySelector(`link[href="${href}"]`)) {
      const newLink = document.createElement('link');
      Array.from(link.attributes).forEach((attr) => {
        newLink.setAttribute(attr.name, attr.value);
      });
      targetElement.appendChild(newLink);
    }
  });

  // Handle style tags
  const styles = tempDiv.querySelectorAll('style');
  styles.forEach((style) => {
    const newStyle = document.createElement('style');
    newStyle.innerHTML = style.innerHTML;
    targetElement.appendChild(newStyle);
  });

  // Handle scripts sequentially with proper initialization checks
  const scripts = Array.from(tempDiv.querySelectorAll('script'));
  const loadedScripts = new Map<string, Promise<void>>();

  // Helper to check if a script is ready
  const waitForScriptReady = (src: string): Promise<void> => {
    return new Promise((resolve) => {
      // If script is already in our tracking map, wait for it
      if (loadedScripts.has(src)) {
        loadedScripts.get(src)?.then(resolve);
        return;
      }

      // If script exists in document but not in our map, create a check
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        // For existing scripts, we'll add a small delay to ensure initialization
        setTimeout(resolve, 100);
        return;
      }

      resolve();
    });
  };

  // First pass: Load all external scripts and track their loading
  for (const script of scripts) {
    const src = script.getAttribute('src');
    if (!src || loadedScripts.has(src)) {
      continue; // Skip inline scripts and already tracked scripts
    }
    try {
      // Store the loading promise in our map
      const loadPromise = loadScript(script, targetElement, instanceId, signal);
      loadedScripts.set(src, loadPromise);
      await loadPromise;

      // Add a small delay after each external script to ensure proper initialization
      await new Promise((resolve) => setTimeout(resolve, 100));
    } catch (error) {
      console.error('Error loading external script:', error);
    }
  }

  // Second pass: Execute inline scripts after ensuring their dependencies are ready
  for (const script of scripts) {
    const src = script.getAttribute('src');
    if (src) {
      continue; // Skip external scripts
    }

    try {
      // Check for dependencies using data-depends-on attribute
      const dependsOn = script.getAttribute('data-depends-on');
      if (dependsOn) {
        // Wait for each dependency
        const dependencies = dependsOn.split(',').map((dep) => dep.trim());
        for (const dep of dependencies) {
          await waitForScriptReady(dep);
        }
      }

      // Add a small delay before executing inline script
      await new Promise((resolve) => setTimeout(resolve, 50));
      await loadScript(script, targetElement, instanceId, signal);
    } catch (error) {
      console.error('Error executing inline script:', error);
    }
  }
};

const UserScripts = (_props: ScriptsProps): JSX.Element => {
  const { page } = useSitecore();
  const isPageEditing = page.mode.isEditing;
  const javaScript = _props?.fields?.javaScript?.value as string;
  const itemName = page.layout.sitecore?.route?.name || 'Script Component';
  const placement = _props?.params?.userScriptType?.toLowerCase() || 'head';
  const instanceIdRef = useRef(`userscripts-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (typeof window === 'undefined' || !javaScript) return;

    const targetElement = placement === 'head' ? document.head : document.body;
    const controller = new AbortController();
    const instanceId = instanceIdRef.current;

    injectResources(javaScript, targetElement, instanceId, controller.signal).catch((error) => {
      console.error('Error injecting resources:', error);
    });

    return () => {
      controller.abort();
      // Phaser cleanup: destroy games and stop WebAudio if present
      try {
        const phaserNs = (window as unknown as { Phaser?: PhaserNamespaceLike }).Phaser;
        const games: PhaserGameLike[] = Array.isArray(phaserNs?.GAMES)
          ? [...(phaserNs!.GAMES as PhaserGameLike[])]
          : [];
        for (const game of games) {
          try {
            if (game?.sound) {
              try {
                if (typeof game.sound.stopAll === 'function') {
                  game.sound.stopAll();
                }
                if (typeof game.sound.removeAll === 'function') {
                  game.sound.removeAll();
                }
                const ctx = game.sound.context;
                if (ctx) {
                  if (typeof ctx.close === 'function') {
                    void Promise.resolve(ctx.close()).catch(() => {});
                  } else if (typeof ctx.suspend === 'function') {
                    void Promise.resolve(ctx.suspend()).catch(() => {});
                  }
                }
              } catch {}
            }
            if (typeof game?.destroy === 'function') {
              game.destroy(true);
            }
          } catch {
            // ignore per-game errors
          }
        }
      } catch {
        // Phaser not present or not needed
      }
      document
        .querySelectorAll<HTMLElement>(`script[data-userscripts-id="${instanceId}"]`)
        .forEach((el) => el.parentElement?.removeChild(el));
    };
  }, [javaScript, placement]);

  if (isPageEditing) {
    return <div>Script Component {itemName}</div>;
  }

  if (!javaScript) {
    return <></>;
  }

  return <></>;
};

export default UserScripts;
